import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IApiErrorBody, IErrorState } from "../app/interfaces/IApiResponses";
import { RootState } from "../app/store";
import { getCompanyEmployees, inviteUser, setCompanyEmployeeRole } from "../app/apis/subRatingsAPI";
import { AxiosError } from "axios";

export type CompanyRole = 'admin' | 'member';

export interface ICompanyEmployee {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    isAdmin: boolean;
    role: CompanyRole;
    // recentInviteStatus is not stored serverside. This is populated only from the current session's activity.
    recentInviteStatus?: 'sent' | 'pending' | 'error';
}

export interface CompanyEmployeesState {
    status: 'idle' | 'loading' | 'failed';
    data: Array<ICompanyEmployee>;
    // The employee whose role is mid-change, so only their row goes busy.
    roleChangingUserId: undefined | number;
    error: undefined | IErrorState;
}

const initialState: CompanyEmployeesState = {
    status: 'idle',
    data: [],
    roleChangingUserId: undefined,
    error: undefined
};

export const getCompanyEmployeesThunk = createAsyncThunk(
    'companyEmployees/getCompanyEmployees',
    async (companyId: number, { rejectWithValue }) => {
        try {
            return await getCompanyEmployees(companyId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const invite = createAsyncThunk(
    'companyEmployees/invite',
    async (
        { companyId, userId }: { companyId: number, userId: number},
        { rejectWithValue }
    ) => {
        try {
            return await inviteUser(companyId, userId);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const setCompanyEmployeeRoleThunk = createAsyncThunk(
    'companyEmployees/setRole',
    async (
        { companyId, userId, role }: { companyId: number, userId: number, role: CompanyRole },
        { rejectWithValue }
    ) => {
        try {
            return await setCompanyEmployeeRole(companyId, userId, role);
        } catch (err) {
            const error = err as AxiosError<IApiErrorBody>;
            return rejectWithValue({ reason: error.response?.data['reason'], status: error.response?.status });
        }
    }
);

export const companyEmployeesSlice = createSlice({
    name: 'companyEmployees',
    initialState,
    reducers: {
        clearCompanyEmployeesError: (state) => {
            state.error = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCompanyEmployeesThunk.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getCompanyEmployeesThunk.fulfilled, (state, action) => {
                state.status = 'idle';
                state.data = action.payload as Array<ICompanyEmployee>;
            })
            .addCase(getCompanyEmployeesThunk.rejected, (state, action) => {
                state.status = 'failed';
                const errorResponse = action?.payload as IErrorState;
                state.error = errorResponse;
            })
            .addCase(invite.pending, (state, action) => {
                const invitedEmployeePosition = state.data.findIndex((employee) => employee.id === action.meta.arg.userId);
                if (invitedEmployeePosition !== -1) {
                    state.data[invitedEmployeePosition].recentInviteStatus = 'pending';
                }
            })
            .addCase(invite.fulfilled, (state, action) => {
                const invitedEmployeePosition = state.data.findIndex((employee) => employee.id === action.meta.arg.userId);
                if (invitedEmployeePosition !== -1) {
                    state.data[invitedEmployeePosition].recentInviteStatus = 'sent';
                }
            })
            .addCase(invite.rejected, (state, action) => {
                const invitedEmployeePosition = state.data.findIndex((employee) => employee.id === action.meta.arg.userId);
                if (invitedEmployeePosition !== -1) {
                    state.data[invitedEmployeePosition].recentInviteStatus = 'error';
                }
            })
            .addCase(setCompanyEmployeeRoleThunk.pending, (state, action) => {
                state.error = undefined;
                state.roleChangingUserId = action.meta.arg.userId;
            })
            .addCase(setCompanyEmployeeRoleThunk.fulfilled, (state, action) => {
                const updated = action.payload as ICompanyEmployee;
                state.roleChangingUserId = undefined;
                state.data = state.data.map(employee => employee.id === updated.id
                    // The role endpoint knows nothing about this session's invites.
                    ? { ...employee, ...updated, recentInviteStatus: employee.recentInviteStatus }
                    : employee
                );
            })
            .addCase(setCompanyEmployeeRoleThunk.rejected, (state, action) => {
                state.roleChangingUserId = undefined;
                state.error = action?.payload as IErrorState;
            })
    }
});

export const { clearCompanyEmployeesError } = companyEmployeesSlice.actions;

export const selectCompanyEmployees = (state: RootState) => state.companyEmployees.data;
export const selectCompanyEmployeesState = (state: RootState) => state.companyEmployees;

export default companyEmployeesSlice.reducer;
